import React, { useRef, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { useSelector } from "react-redux";

import FileIcon from "~/components/file-type-icon";
import DocumentDescription from "~/components/document-description";
import { downloadFileHandler, openPreviewInNewTab } from "~/utils/fileUtils";
const NO_DESCRIPTION_PLACEHOLDER = "No description available";
const DOCUMENT_TABLE_SORT_FIELDS = {
  NAME: "name",
  DESCRIPTION: "description",
  SUBCATEGORY: "subCategory",
  LAST_MODIFIED: "createdAt",
};
import Categories from "~/components/Processes/conversations/utils/categories.json";
import EmptyDataView from "~/components/nodataview";
import fileIcon from "~/assets/empty-file.svg";
import "~/Pages/Process/components/tabs/attachments/attachments.scss";

const getSubCategoryName = (categoryCode, subCategoryCode) => {
  const cat = Categories?.find((c) => c.code === categoryCode);
  const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
  return sub?.name ?? subCategoryCode ?? "-";
};

const getContainer = (blobUrl) =>
  blobUrl?.includes("/ess") ? "ess" : "documents";

const safeDecode = (text) => {
  if (text == null || text === "") return text;
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

export default function DocumentTable({
  value = [],
  loading = false,
  formatDate,
  onDelete,
  relatedId,
  relatedType,
  totalRecords = 0,
  rows = 10,
  first = 0,
  onPageChange,
  onSortOrderChange,
  isFilterApplied = false,
  isPayslipFolder = false,
  emptyMessage = "No files found",
  filterEmptyHeader = "It looks like there are no results based on the filters you applied",
  filterEmptyDescription = "No matches were found. Please adjust your filters to improve your search.",
  /** Custom download handler; if provided, used instead of default. Signature: (row) => Promise|void */
  onDownload,
  /** Optional subtext rendered below file name, e.g. breadcrumb. Signature: (row) => ReactNode */
  nameSubtext,
  /** Called after description is updated; use to refetch the list. */
  onRefetch,
}) {
  const menuRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [descriptionOverrides, setDescriptionOverrides] = useState({});

  const getRowKey = (row) => row?.blobName || row?.blob_url || row?.id || "";
  const { id: userId, role } = useSelector((state) => state.login);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState(1);

  const canDelete = (row) =>
    role?.startsWith("GXM") ||
    (!isPayslipFolder && row.created_by === userId) ||
    (row?.isPayslipData && role?.includes(row.owner)) ||
    (isPayslipFolder && row.user_id === userId);

  const handleMenuToggle = (e, rowData) => {
    setSelectedRow(rowData);
    menuRef.current?.toggle(e);
  };

  const container = (row) => getContainer(row?.blob_url);
  const openPreview = (row) =>
    openPreviewInNewTab({ objectURL: row?.blob_url }, container(row));
  const download = (row) => {
    if (onDownload) {
      onDownload(row);
    } else {
      downloadFileHandler(
        { blob_url: row?.blob_url, name: row?.name },
        container(row),
      );
    }
  };

  const menuItems = [
    {
      label: "Preview File",
      icon: "pi pi-eye",
      command: () => selectedRow && openPreview(selectedRow),
    },
    {
      label: "View File Details",
      icon: "pi pi-info-circle",
      command: () => {
        if (selectedRow) {
          setDetailsRow(selectedRow);
          setSelectedRow(null);
        }
      },
    },
    {
      label: "Download",
      icon: "pi pi-download",
      command: () => selectedRow && download(selectedRow),
    },
    ...(selectedRow && canDelete(selectedRow)
      ? [
          { separator: true },
          {
            label: "Delete File",
            icon: "pi pi-trash",
            command: () => onDelete?.(selectedRow),
          },
        ]
      : []),
  ];

  const useServerSort = Boolean(onSortOrderChange);
  const sortedValue = useMemo(() => {
    if (useServerSort || !value?.length || !sortField) return value;
    const order = sortOrder === 1 ? "ASC" : "DESC";
    return [...value].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === DOCUMENT_TABLE_SORT_FIELDS.DESCRIPTION) {
        aVal = (aVal && String(aVal).trim()) || "\uFFFF";
        bVal = (bVal && String(bVal).trim()) || "\uFFFF";
      } else {
        aVal = aVal ?? "";
        bVal = bVal ?? "";
      }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
      });
      return order === "ASC" ? cmp : -cmp;
    });
  }, [value, sortField, sortOrder, useServerSort]);

  const skeletonRows = [...Array(5)];

  if (loading) {
    return (
      <DataTable
        value={skeletonRows}
        tableStyle={{ minWidth: "50rem" }}
        className="datatable-right-align"
      >
        <Column header="Name" body={() => <Skeleton width="100%" />} />
        <Column header="Description" body={() => <Skeleton />} />
        <Column header="Subcategory" body={() => <Skeleton />} />
        <Column header="Last Modified" body={() => <Skeleton />} />
        <Column body={() => <Skeleton />} />
      </DataTable>
    );
  }

  if (isFilterApplied && !value?.length) {
    return (
      <EmptyDataView
        Icon={<img src={fileIcon} alt="File Icon" />}
        Header={filterEmptyHeader}
        Description={filterEmptyDescription}
      />
    );
  }

  return (
    <>
      <DataTable
        value={sortedValue}
        tableStyle={{ minWidth: "50rem" }}
        className="datatable-right-align"
        paginator={
          (totalRecords > 0 ? totalRecords : sortedValue.length) > rows
        }
        rows={rows}
        rowsPerPageOptions={[10, 20, 30]}
        first={first}
        totalRecords={
          totalRecords > 0 && totalRecords !== sortedValue.length
            ? totalRecords
            : undefined
        }
        lazy={totalRecords > 0 && totalRecords !== sortedValue.length}
        onPage={onPageChange}
        emptyMessage={emptyMessage}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
          onSortOrderChange?.(e.sortOrder === 1 ? "ASC" : "DESC");
        }}
      >
        <Column
          header="Name"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.NAME}
          body={(row) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <div className="file__name">
                <FileIcon
                  isPayslipFolder={isPayslipFolder}
                  filetype={
                    isPayslipFolder ? "application/pdf" : row?.file_type
                  }
                  filename={safeDecode(row?.name ?? "")}
                  downloadFile={() => download(row)}
                />
              </div>
              {nameSubtext && nameSubtext(row)}
            </div>
          )}
        />
        <Column
          header="Description"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.DESCRIPTION}
          body={(row) => {
            const key = getRowKey(row);
            const displayDesc =
              key && descriptionOverrides[key] !== undefined
                ? descriptionOverrides[key]
                : row?.description;
            return (
              <div className="flex align-items-center justify-content-between">
                <span className="file-name-ellipsis">
                  {displayDesc?.trim()
                    ? safeDecode(displayDesc)
                    : NO_DESCRIPTION_PLACEHOLDER}
                </span>
                <span
                  className="pi pi-angle-right cursor-pointer"
                  onClick={() => setDetailsRow(row)}
                  role="button"
                  aria-label="View description details"
                />
              </div>
            );
          }}
        />
        <Column
          header="Subcategory"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.SUBCATEGORY}
          body={(row) => (
            <span
              style={{
                textDecoration: "none",
                color: "inherit",
                cursor: "default",
              }}
            >
              {getSubCategoryName(row?.category, row?.subCategory)}
            </span>
          )}
        />
        <Column
          header="Last Modified"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.LAST_MODIFIED}
          body={(row) => (
            <span>{formatDate?.(row?.updatedAt || row?.createdAt) ?? "-"}</span>
          )}
        />
        <Column
          className="user_action--col"
          body={(row) => (
            <div className="flex align-items-center">
              <span
                className="pi pi-info-circle cursor-pointer pencil-pointer"
                onClick={() => setDetailsRow(row)}
                role="button"
                aria-label="View file details"
              />
              <span
                className="pi pi-download pencil-pointer"
                onClick={() => download(row)}
                role="button"
                aria-label="Download"
              />
              <Menu
                model={menuItems}
                popup
                ref={menuRef}
                id={`doc_menu_${row?.id ?? row?.name}`}
              />
              {canDelete(row) && (
                <span
                  className="pi pi-ellipsis-v pencil-pointer"
                  onClick={(e) => handleMenuToggle(e, row)}
                  role="button"
                  aria-haspopup
                  aria-controls={`doc_menu_${row?.id ?? row?.name}`}
                  aria-label="Actions"
                />
              )}
            </div>
          )}
        />
      </DataTable>

      {detailsRow && (
        <DocumentDescription
          data={detailsRow}
          id={relatedId}
          type={relatedType}
          visible={!!detailsRow}
          onVisibleChange={(v) => !v && setDetailsRow(null)}
          hideTrigger
          onDescriptionUpdated={(row, newDesc) => {
            const key = getRowKey(row);
            if (key) {
              setDescriptionOverrides((prev) => ({
                ...prev,
                [key]: newDesc,
              }));
            }
            onRefetch?.();
            setDetailsRow(null);
          }}
        />
      )}
    </>
  );
}
